import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { HexField } from "@/components/HexField";
import { LangSwitcher } from "@/components/LangSwitcher";
import { LegalLinks } from "@/components/LegalLinks";
import { Wordmark } from "@/components/Nav";

const fade = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.2, 0.7, 0.2, 1] } } };

export default function Landing() {
  const { config, user } = useAuth();
  const { t } = useLang();
  const sections = [
    { n: "01", title: t("landing.s1_t"), desc: t("landing.s1_d"), result: t("landing.r1") },
    { n: "02", title: t("landing.s2_t"), desc: t("landing.s2_d"), result: t("landing.r2") },
    { n: "03", title: t("landing.s3_t"), desc: t("landing.s3_d"), result: t("landing.r3") },
  ];
  return (
    <div className="grain relative min-h-screen overflow-x-hidden" data-testid="landing-page">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 2, ease: "easeOut" }}
        className="pointer-events-none absolute right-0 top-0 hidden w-[58vw] max-w-[1150px] opacity-[.45] lg:block" data-testid="landing-hex">
        <HexField className="h-auto w-full" />
      </motion.div>
      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-6">
          <span className="hidden font-mono text-xs text-vox-accent sm:inline">01</span>
          <Wordmark brand={config.brand} to="/" />
        </div>
        <div className="flex items-center gap-3">
          <LangSwitcher compact />
          <Link to={user ? "/dashboard" : "/login"} className="btn-ghost" data-testid="landing-login-link">{user ? t("nav.dashboard") : t("nav.login")}</Link>
        </div>
      </header>

      <section className="relative z-10 mx-auto flex min-h-[78vh] max-w-6xl flex-col justify-center px-6 pb-24 pt-10">
        <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.12 } } }} className="relative">
          <motion.div variants={fade} className="mb-8 flex items-center gap-3">
            <span className="relative grid h-3 w-3 place-items-center"><span className="absolute h-1.5 w-1.5 rounded-full bg-vox-bright animate-breathe" /></span>
            <span className="mono-label">{t("landing.eyebrow")}</span>
          </motion.div>
          <motion.h1 variants={fade} className="max-w-4xl text-4xl font-semibold leading-[1.05] tracking-tight text-vox-text sm:text-5xl lg:text-7xl" data-testid="landing-title">
            {t("landing.title")}
          </motion.h1>
          <motion.p variants={fade} className="mt-8 max-w-2xl text-base leading-relaxed text-vox-muted md:text-lg">
            {t("landing.subtitle")}
          </motion.p>
          <motion.div variants={fade} className="mt-12 flex flex-wrap items-center gap-4">
            <Link to={user ? "/dashboard" : "/register"} className="btn-primary !px-7 !py-3" data-testid="landing-cta-register">
              {t("landing.cta")} <ArrowRight size={16} />
            </Link>
            {!user && <Link to="/login" className="btn-ghost !px-7 !py-3" data-testid="landing-cta-login">{t("landing.cta2")}</Link>}
          </motion.div>
        </motion.div>
        <div className="absolute bottom-8 left-6 hidden h-16 w-px overflow-hidden bg-vox-line md:block">
          <span className="block h-full w-full bg-vox-bright animate-scroll-line" />
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-6xl px-6 pb-32">
        {sections.map((s, i) => (
          <motion.article
            key={s.n}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
            className="grid gap-8 border-t border-vox-line py-20 md:grid-cols-[220px_1fr] md:py-28"
            data-testid={`landing-section-${s.n}`}
          >
            <div className="flex items-start gap-4">
              <span className="font-mono text-sm text-vox-accent">{s.n} / 03</span>
              <span className="mono-label !text-vox-dim pt-0.5">{t("landing.sectionLabel")}</span>
            </div>
            <div>
              <h2 className="max-w-3xl text-2xl font-semibold leading-tight tracking-tight text-vox-text sm:text-3xl lg:text-5xl">{s.title}</h2>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-vox-muted md:text-lg">{s.desc}</p>
              <p className="mt-8 flex items-center gap-3 text-sm text-vox-bright">
                <ArrowUpRight size={16} /> <span className="mono-label">{t("landing.result")}:</span> <span className="text-vox-text">{s.result}</span>
              </p>
            </div>
          </motion.article>
        ))}
        {i18nSpacer}
      </section>

      <footer className="relative z-10 border-t border-vox-line">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-14 md:flex-row md:items-end md:justify-between">
          <span className="font-sans text-6xl font-bold tracking-tight text-vox-line md:text-8xl">{config.brand}</span>
          <div className="flex flex-col gap-3 md:items-end">
            <span className="mono-label !text-vox-dim">{t("landing.footer")} · 2026</span>
            {config.operator?.operator_name && <span className="text-xs text-vox-dim" data-testid="landing-operator-name">{config.operator.operator_name}</span>}
            <LegalLinks />
          </div>
        </div>
      </footer>
    </div>
  );
}

const i18nSpacer = null;
